/**
 * The formant synthesizer (audio-direction §5): renders a babble plan (babble.ts) as a Web Audio graph. A glottal
 * source (a band-limited pulse with the speaker's spectral tilt, following the pitch contour with vibrato and
 * jitter) plus aspiration noise feeds three parallel formant resonators whose frequencies glide between the
 * segment targets (coarticulation); a separate noise channel shapes plosive bursts and fricatives. Every
 * parameter moves along curves at the control rate. Pure recipe (plan → graph) and deterministic.
 */
import { createRng, nextFloat } from '../../core/rng'
import { noiseSource } from '../sfx/dsp'
import type { OfflineFactory } from '../sfx/render'
import { normalizeSet } from '../sfx/render'
import type { Utterance } from './babble'
import type { CommsConfig } from './commsSchema'

/** A glottal-pulse-like periodic wave: harmonic n at 1 / n^tilt. */
export function glottalWave(ctx: BaseAudioContext, tilt: number, harmonics = 48): PeriodicWave {
  const real = new Float32Array(harmonics + 1)
  const imag = new Float32Array(harmonics + 1)
  for (let n = 1; n <= harmonics; n++) imag[n] = 1 / Math.pow(n, tilt)
  return ctx.createPeriodicWave(real, imag)
}

interface Curves {
  pitch: Float32Array<ArrayBuffer>
  voiced: Float32Array<ArrayBuffer>
  breath: Float32Array<ArrayBuffer>
  noise: Float32Array<ArrayBuffer>
  noiseHz: Float32Array<ArrayBuffer>
  noiseQ: Float32Array<ArrayBuffer>
  f: [Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>]
  q: [Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>]
}

/** Samples the plan into control-rate curves (smoothed: voicing ramps, formant glides). */
export function planCurves(u: Utterance, cfg: CommsConfig): Curves {
  const rate = cfg.prosody.controlRate
  const n = Math.max(2, Math.ceil(u.seconds * rate) + 1)
  const mk = (): Float32Array<ArrayBuffer> => new Float32Array(n)
  const c: Curves = { pitch: mk(), voiced: mk(), breath: mk(), noise: mk(), noiseHz: mk(), noiseQ: mk(), f: [mk(), mk(), mk()], q: [mk(), mk(), mk()] }
  const rng = createRng((u.voice.seed * 2654435761) ^ Math.round(u.seconds * 1000))
  const dt = 1 / rate
  const env = 1 - Math.exp(-dt / 0.008)
  const burst = 1 - Math.exp(-dt / 0.002)
  const glide = 1 - Math.exp(-dt / cfg.formants.transition)
  const bw = cfg.formants.bandwidths
  let seg = 0
  let v = 0
  let nz = 0
  let jit = 0
  let lastPitch = u.segments.length > 0 ? u.segments[0].p0 : u.voice.pitch
  let lastNoiseHz = 1000
  let lastNoiseQ = 1
  const f = [0, 0, 0]
  for (let i = 0; i < n; i++) {
    const t = i * dt
    while (seg < u.segments.length - 1 && t >= u.segments[seg].start + u.segments[seg].dur) seg++
    const s = u.segments[seg]
    const inside = s !== undefined && t < s.start + s.dur
    const k = inside ? (t - s.start) / Math.max(s.dur, 1e-6) : 1
    if (s !== undefined && inside && s.voice > 0) lastPitch = s.p0 + (s.p1 - s.p0) * k
    // Jitter: a slow random walk; vibrato: a gentle sine.
    jit += (nextFloat(rng) * 2 - 1) * u.voice.jitter * 0.5 - jit * 0.1
    c.pitch[i] = lastPitch * (1 + jit + cfg.prosody.vibratoDepth * Math.sin(2 * Math.PI * cfg.prosody.vibratoHz * t))
    const targetV = inside ? s.voice * u.level : 0
    v += (targetV - v) * env
    c.voiced[i] = v
    c.breath[i] = v * u.voice.breath
    const targetN = inside ? s.noise * u.level : 0
    nz += (targetN - nz) * (targetN > nz ? burst : env)
    c.noise[i] = nz
    if (inside && s.noiseHz > 0) {
      lastNoiseHz = s.noiseHz
      lastNoiseQ = s.noiseQ
    }
    c.noiseHz[i] = lastNoiseHz
    c.noiseQ[i] = lastNoiseQ
    if (s !== undefined) {
      const tf = [s.f1, s.f2, s.f3]
      for (let j = 0; j < 3; j++) {
        f[j] = i === 0 ? tf[j] : f[j] + (tf[j] - f[j]) * glide
        c.f[j][i] = f[j]
        c.q[j][i] = f[j] / bw[j]
      }
    }
  }
  return c
}

/** Builds the utterance graph into `ctx`, starting at `t0`, feeding `out`. */
export function buildUtterance(ctx: BaseAudioContext, out: AudioNode, u: Utterance, cfg: CommsConfig, t0: number): void {
  const c = planCurves(u, cfg)
  const dur = Math.max(u.seconds, 2 / cfg.prosody.controlRate)
  const rng = createRng(u.voice.seed ^ 0xb00b5)
  const osc = ctx.createOscillator()
  osc.setPeriodicWave(glottalWave(ctx, u.voice.tilt))
  osc.frequency.setValueCurveAtTime(c.pitch, t0, dur)
  const voiced = ctx.createGain()
  voiced.gain.value = 0
  voiced.gain.setValueCurveAtTime(c.voiced, t0, dur)
  osc.connect(voiced)
  const { src: asp, offset: aspOffset } = noiseSource(ctx, rng, dur + 0.05, 'white')
  const aspGain = ctx.createGain()
  aspGain.gain.value = 0
  aspGain.gain.setValueCurveAtTime(c.breath, t0, dur)
  asp.connect(aspGain)
  const src = ctx.createGain()
  voiced.connect(src)
  aspGain.connect(src)
  const mix = ctx.createGain()
  for (let j = 0; j < 3; j++) {
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.setValueCurveAtTime(c.f[j], t0, dur)
    bp.Q.setValueCurveAtTime(c.q[j], t0, dur)
    const g = ctx.createGain()
    // Narrow resonators lose level: make up with the square root of Q (keeps the vowels balanced).
    g.gain.value = cfg.formants.gains[j] * Math.sqrt(c.q[j][0] > 0 ? c.q[j][0] : 1)
    src.connect(bp)
    bp.connect(g)
    g.connect(mix)
  }
  const { src: hiss, offset: hissOffset } = noiseSource(ctx, rng, dur + 0.05, 'white')
  const hissBp = ctx.createBiquadFilter()
  hissBp.type = 'bandpass'
  hissBp.frequency.setValueCurveAtTime(c.noiseHz, t0, dur)
  hissBp.Q.setValueCurveAtTime(c.noiseQ, t0, dur)
  const hissGain = ctx.createGain()
  hissGain.gain.value = 0
  hissGain.gain.setValueCurveAtTime(c.noise, t0, dur)
  hiss.connect(hissBp)
  hissBp.connect(hissGain)
  hissGain.connect(mix)
  mix.connect(out)
  osc.start(t0)
  asp.start(t0, aspOffset)
  hiss.start(t0, hissOffset)
  osc.stop(t0 + dur + 0.02)
  asp.stop(t0 + dur + 0.02)
  hiss.stop(t0 + dur + 0.02)
}

/** Renders an utterance offline (mono) and normalises it to −3 dBFS. */
export async function renderUtterance(factory: OfflineFactory, u: Utterance, cfg: CommsConfig, sampleRate: number): Promise<AudioBuffer> {
  const lead = 0.01
  const ctx = factory(1, Math.ceil((u.seconds + lead + 0.08) * sampleRate), sampleRate)
  buildUtterance(ctx, ctx.destination, u, cfg, lead)
  const b = await ctx.startRendering()
  normalizeSet([b], -3)
  return b
}
